// Milepost strip — split into two aligned panels for clarity:
//   TOP:    crashes along the corridor (severity-colored, stacked when close)
//   BOTTOM: the selected analysis' deficiency metric + run bands
// Both share the same milepost domain so vertical alignment reads directly.
// Recharts ComposedChart (style constants mirror UsageDashboard.jsx).
import React, { useMemo } from 'react';
import {
  ComposedChart, Bar, Scatter, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceArea, ResponsiveContainer,
} from 'recharts';

const AXIS_TICK = { fill: '#94a3b8', fontSize: 12 };
const TOOLTIP_STYLE = {
  backgroundColor: '#09101b',
  border: '1px solid #334155',
  borderRadius: '0.5rem',
  color: '#e2e8f0',
};

const METRIC_LABEL = {
  SSD: 'speed deficit (mph)',
  PSD: 'speed deficit (mph)',
  GLR: 'glare minutes / yr',
  FSA: 'cross slope (%)',
  HCA: 'curve safety score (%)',
  GRW: 'embankment height (ft)',
  ISD: 'required ISD (ft)',
  ALL: 'deficiency runs',
};
const ANALYSIS_RUN_COLORS = {
  SSD: '#c92a2a', PSD: '#9c36b5', GLR: '#e8a30c', FSA: '#1971c2', HCA: '#e8590c',
  GRW: '#5f3dc4', ISD: '#0ca678',
};
const CRASH_COLORS = { fatal: '#7f0000', injury: '#e03131', pdo: '#868e96', unknown: '#adb5bd' };

export default function StripChart({ analysisByDirection, crashes, runs, filters, corridorExtent }) {
  const analysis = filters.analysis;
  const isAll = analysis === 'ALL';

  const domain = corridorExtent
    ? [Math.floor(corridorExtent[0]), Math.ceil(corridorExtent[1])]
    : ['auto', 'auto'];

  const data = useMemo(() => {
    if (isAll) {
      // ALL mode has no single metric: draw an invisible baseline so the axes
      // render; the runs' ReferenceAreas carry the content.
      return (corridorExtent || [0, 1]).map((mp) => ({ mp, value: 0 }));
    }
    const byMp = new Map();
    for (const [dir, payload] of Object.entries(analysisByDirection || {})) {
      if (!payload) continue;
      if (filters.direction !== 'both' && dir !== filters.direction) continue;
      for (const bin of payload.strip?.bins || []) {
        if (filters.mpRange && (bin.mp < filters.mpRange[0] || bin.mp > filters.mpRange[1])) continue;
        const prev = byMp.get(bin.mp);
        // keep the worse value across directions (negative-is-worse for
        // SSD/PSD/FSA cross slope; GLR positive-is-worse; HCA lower-is-worse)
        const worse = prev === undefined
          ? bin.value
          : analysis === 'GLR' ? Math.max(prev, bin.value) : Math.min(prev, bin.value);
        byMp.set(bin.mp, worse);
      }
    }
    return [...byMp.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([mp, value]) => ({
        mp,
        value: analysis === 'GLR' || analysis === 'HCA' ? value : Math.abs(value),
        raw: value,
      }));
  }, [analysisByDirection, filters, analysis, isAll, corridorExtent]);

  // top panel: crashes, stacked vertically when they share a milepost bucket
  const crashPoints = useMemo(() => {
    const seen = {};
    return (crashes || []).map((c) => {
      const bucket = Math.round(c.milepost * 12);      // ~0.083 mi
      const level = (seen[bucket] = (seen[bucket] || 0) + 1);
      return { mp: c.milepost, y: level, severityClass: c.severityClass, isAnimal: c.isAnimal };
    });
  }, [crashes]);
  const maxStack = Math.max(3, ...crashPoints.map((p) => p.y));

  const runAreas = (runs || []).map((run) => (
    <ReferenceArea key={`${run.analysis || analysis}-${run.direction}-${run.id}`}
      x1={run.mpStart} x2={Math.max(run.mpEnd, run.mpStart + 0.01)}
      fill={isAll ? (ANALYSIS_RUN_COLORS[run.analysis] || '#c92a2a') : '#c92a2a'}
      fillOpacity={isAll ? 0.28 : 0.12} />
  ));

  if (!data.length && !crashPoints.length) {
    return <div className="safety-strip-empty">No strip data for this selection.</div>;
  }

  return (
    <div className="safety-strip-split">
      {crashPoints.length > 0 && (
        <div>
          <div className="safety-strip-label">Crashes</div>
          <ResponsiveContainer width="100%" height={90}>
            <ComposedChart data={[{ mp: domain[0], y: 0 }, { mp: domain[1], y: 0 }]}
              margin={{ top: 6, right: 16, bottom: 0, left: 0 }}
              syncId="safety-strip">
              <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="mp" type="number" domain={domain} hide />
              <YAxis domain={[0, maxStack + 1]} width={44} tick={false}
                label={{ value: 'crashes', angle: -90, position: 'insideLeft', fill: '#64748b', fontSize: 10 }} />
              <Tooltip contentStyle={TOOLTIP_STYLE}
                formatter={() => ['crash', '']}
                labelFormatter={(mp) => `MP ${Number(mp).toFixed(2)}`} />
              <Scatter name="crash" data={crashPoints} dataKey="y"
                shape={(props) => (
                  <circle cx={props.cx} cy={props.cy} r={3.4}
                    fill={props.payload.isAnimal ? 'none' : CRASH_COLORS[props.payload.severityClass]}
                    stroke={props.payload.isAnimal ? '#9aa7ac' : 'none'}
                    strokeWidth={props.payload.isAnimal ? 1.4 : 0} />
                )} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
      <div>
        <div className="safety-strip-label">
          {isAll ? 'Deficiency runs (colored by analysis)' : `${analysis} deficiencies`}
        </div>
        <ResponsiveContainer width="100%" height={190}>
          <ComposedChart data={data} margin={{ top: 4, right: 16, bottom: 4, left: 0 }}
            syncId="safety-strip">
            <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
            <XAxis dataKey="mp" type="number" domain={domain} tick={AXIS_TICK}
              label={{ value: 'Milepost', position: 'insideBottomRight', offset: -2, fill: '#64748b', fontSize: 11 }} />
            <YAxis tick={AXIS_TICK} width={44}
              label={{ value: METRIC_LABEL[analysis], angle: -90, position: 'insideLeft', fill: '#64748b', fontSize: 11 }} />
            <Tooltip
              contentStyle={TOOLTIP_STYLE}
              formatter={(v) => [v, METRIC_LABEL[analysis]]}
              labelFormatter={(mp) => `MP ${Number(mp).toFixed(2)}`}
            />
            {runAreas}
            {!isAll && <Bar dataKey="value" fill="#e8590c" barSize={2} isAnimationActive={false} />}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
